import { useState, useCallback } from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useUploadDocumentMutation } from '@/store/documentsApi';
import { useGetCurrentTenantQuery } from '@/store/tenantApi';
import DocumentUploadModal, { type FileWithName, type UploadAssignment } from './DocumentUploadModal';
import FileUploadZone from './FileUploadZone';
import {
  UploadUsageInfo,
  UploadUsageBarTrack,
  UploadUsageBarFill,
} from './DocumentUpload.styled';

interface DocumentUploadProps {
  onUploadComplete?: () => void;
}

export default function DocumentUpload({ onUploadComplete }: DocumentUploadProps) {
  const { t } = useTranslation();
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [uploadDocument] = useUploadDocumentMutation();
  const { data: tenant, isLoading: tenantLoading } = useGetCurrentTenantQuery();

  const maxDocuments = tenant?.settings?.maxDocuments ?? null;
  const currentCount = tenant?.currentDocumentCount ?? 0;
  const limitReached = maxDocuments !== null && currentCount >= maxDocuments;
  const usagePercent = maxDocuments !== null ? (currentCount / maxDocuments) * 100 : 0;

  const handleUploadFile = useCallback(
    async (file: File) => {
      await uploadDocument({
        file,
        fileName: file.name,
      }).unwrap();
    },
    [uploadDocument],
  );

  const handleFilesAccepted = useCallback((files: File[]) => {
    setPendingFiles(files);
    setShowModal(true);
  }, []);

  const handleModalConfirm = useCallback(
    async (fileEntries: FileWithName[], assignment: UploadAssignment) => {
      setShowModal(false);
      setPendingFiles([]);
      let anySuccess = false;
      for (const entry of fileEntries) {
        try {
          await uploadDocument({
            file: entry.file,
            fileName: entry.file.name,
            documentName: entry.documentName,
            customerId: assignment.customerId,
            sprawaId: assignment.sprawaId,
          }).unwrap();
          anySuccess = true;
        } catch {
          // Error handled by the mutation state
        }
      }
      if (anySuccess) {
        onUploadComplete?.();
      }
    },
    [uploadDocument, onUploadComplete],
  );

  const handleModalCancel = useCallback(() => {
    setShowModal(false);
    setPendingFiles([]);
  }, []);

  return (
    <div>
      {tenantLoading && (
        <UploadUsageInfo>
          <Spinner animation="border" size="sm" />
        </UploadUsageInfo>
      )}
      {tenant && (
        <UploadUsageInfo>
          <span>
            {maxDocuments !== null
              ? t('upload.usage', { current: currentCount, max: maxDocuments })
              : t('upload.usageUnlimited', { current: currentCount })}
          </span>
          {maxDocuments !== null && (
            <UploadUsageBarTrack>
              <UploadUsageBarFill $percent={usagePercent} $warning={usagePercent >= 80} />
            </UploadUsageBarTrack>
          )}
        </UploadUsageInfo>
      )}

      {limitReached && (
        <Alert variant="warning" className="mb-3">
          {t('upload.limitReached')}
        </Alert>
      )}

      <FileUploadZone
        onUploadFile={handleUploadFile}
        disabled={limitReached}
        onFilesAccepted={handleFilesAccepted}
        useNamingModal
      />

      <DocumentUploadModal
        files={pendingFiles}
        show={showModal}
        onConfirm={handleModalConfirm}
        onCancel={handleModalCancel}
      />
    </div>
  );
}
